import React from 'react';
import PropTypes from 'prop-types';
import Select2 from '../Select2.jsx';
import FieldErrors, { fieldClass } from './FieldErrors.jsx';

function humanize(name) {
  return name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// Stable reference so Select2 doesn't re-init on every parent render.
const SELECT2_OPTIONS = { minimumResultsForSearch: Infinity };

const percent = (ratio) => `${Math.round(ratio * 100)}%`;

// A labelled 0-1 ratio slider with its value read out as a percentage on the label
// line. Both logo sliders are this control; the help text below each is not, since
// they explain different things.
//
// min/max are the RECOMMENDED range; the track always widens to contain the current
// value. On a fixed track an out-of-range value pins the thumb at the end while the
// readout beside it prints the true value, and the first drag silently rewrites the
// style down into range. Widening is one-way per render: drag back inside the
// recommendation and the track re-narrows, so such a value can be left but never
// re-entered.
//
// Which slider can actually be out of range: logo_clip_padding, whose model bound is
// 0..0.5 against this UI's 0..0.25. logo_size_ratio no longer can — QrCodeTemplate#style
// routes both branches through StyleConfig, which clamps it into LOGO_SIZE_RANGE on
// read, so a pre-cap 5-50% value is healed before it reaches the form. The mechanism
// stays because padding still needs it, and because it is the right behaviour if
// either bound is ever widened again.
function RatioSlider({
  label, value, onChange, min, max, step, testId,
}) {
  return (
    <div className="qr-narrow">
      <label className="qr-label qr-label--inline">
        <span>{label}</span>
        <span className="qr-range-value">{percent(value)}</span>
      </label>
      <input
        type="range"
        className="qr-range"
        min={Math.min(min, value)}
        max={Math.max(max, value)}
        step={step}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
    </div>
  );
}

RatioSlider.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  testId: PropTypes.string.isRequired,
};

class LogoTab extends React.Component {
  constructor(props) {
    super(props);
    // Built once — a fresh array each render makes Select2 loop (see QrStyleForm).
    // The `none` clip means an unclipped (square) logo backing, so it reads as "Square"
    // in the logo-shape picker while keeping the `none` enum value on the wire.
    this.clipData = props.logoClips.map((clip) => ({
      id: clip,
      text: clip === 'none' ? 'Square' : humanize(clip),
    }));
  }

  render() {
    const {
      style, patchStyle, onLogoFile, uploadError, errors,
    } = this.props;
    // Both fall back, because #to_h is sparse in BOTH directions: it omits a key the
    // caller never set, and omit_nil_typed_keys actively drops one whose value came in
    // as an explicit null or an empty form field. Without a fallback the slider gets
    // undefined, min/max compute to NaN, the input flips to uncontrolled, and the
    // readout renders "NaN%". 0.25/0 are the contract defaults (StyleConfig::
    // LEGACY_DEFAULTS, matching Style's own kwarg defaults), so a template that omits
    // the key shows what it will actually render as.
    const sizeRatio = style.logo_size_ratio ?? 0.25;
    const padding = style.logo_clip_padding ?? 0;
    return (
      <div className="qr-logo-tab">
        <div className={fieldClass(errors, 'logo')}>
          <label className="qr-label">Logo image</label>
          {/* No `name` — this never submits raw bytes. onChange lifts the picked
              File so QrStyleForm can upload it and submit the resulting signed id
              via a hidden field instead. */}
          <input
            type="file"
            accept="image/png"
            className="qr-file"
            data-testid="logo-file"
            onChange={(e) => onLogoFile(e.target.files[0] || null)}
          />
          <p className="qr-help">PNG, ≤5MB.</p>
          {uploadError && (
            <p className="qr-help" data-testid="logo-upload-error" style={{ color: 'var(--qr-danger)' }}>
              Upload failed — pick the file again to retry.
            </p>
          )}
          <FieldErrors messages={errors.logo} />
        </div>

        {/* Standalone boolean toggle — checkbox on the left, label weighted like
            the other field labels (not the bold section-head treatment ColorsTab
            uses for its titled color rows). Wrapped in a qr-field so a validation
            message has somewhere to sit; the checkbox itself takes no border. */}
        <div className="qr-field">
          <label className="qr-check-row">
            <input
              type="checkbox"
              data-testid="remove-bg"
              checked={!!style.logo_remove_background}
              onChange={(e) => patchStyle('logo_remove_background', e.target.checked)}
            />
            <span>Hide background dots</span>
          </label>
          <FieldErrors messages={errors.logo_remove_background} />
        </div>

        {/* Each control on its own line. The sliders are kept short (qr-narrow caps
            their width) rather than stretching the full form column.

            This first control sets the reserved AREA the badge occupies, not the size
            of the logo image — a distinction the old "Logo size" label lost, which led
            a reviewer to conclude that padding was inflating the logo. The label
            carries that distinction now; the help text stays short and the live
            preview shows the result. */}
        <div className={fieldClass(errors, 'logo_size_ratio')}>
          <RatioSlider
            label="Logo area"
            testId="logo-size"
            min={0.1}
            max={0.25}
            step={0.05}
            value={sizeRatio}
            onChange={(v) => patchStyle('logo_size_ratio', v)}
          />
          <p className="qr-help">Share of the QR code reserved for the logo.</p>
          <FieldErrors messages={errors.logo_size_ratio} />
        </div>

        <div className={fieldClass(errors, 'logo_clip_padding')}>
          <RatioSlider
            label="Logo padding"
            testId="logo-padding"
            min={0}
            max={0.25}
            step={0.05}
            value={padding}
            onChange={(v) => patchStyle('logo_clip_padding', v)}
          />
          <p className="qr-help">Padding shrinks the logo inside the area.</p>
          <FieldErrors messages={errors.logo_clip_padding} />
        </div>

        <div className={fieldClass(errors, 'logo_clip')}>
          <label className="qr-label">Logo shape</label>
          <Select2
            className="form-control"
            data-testid="logo-clip"
            value={style.logo_clip}
            data={this.clipData}
            options={SELECT2_OPTIONS}
            onChange={(e) => patchStyle('logo_clip', e.target.value)}
          />
          <FieldErrors messages={errors.logo_clip} />
        </div>
      </div>
    );
  }
}

LogoTab.propTypes = {
  style: PropTypes.object.isRequired,
  patchStyle: PropTypes.func.isRequired,
  logoClips: PropTypes.arrayOf(PropTypes.string).isRequired,
  onLogoFile: PropTypes.func,
  uploadError: PropTypes.bool,
  errors: PropTypes.object,
};

LogoTab.defaultProps = { onLogoFile: () => {}, uploadError: false, errors: {} };

export default LogoTab;
